import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaChevronLeft,
  FaCalendarDay,
  FaClock,
  FaStore,
  FaCheck,
  FaArrowRight,
  FaMapMarkerAlt,
} from "react-icons/fa6";
import {
  FaExclamationCircle,
  FaInfoCircle,
  FaShoppingCart,
  FaEdit,
} from "react-icons/fa";
import AddressComponent from "./AddressComponent";
import { createOrder } from "../redux/order/OrderSlice";
import { createHomeSalonOrder } from "../redux/order/OrderSalonHomeSlice";
// import { createProductOrder } from "../redux/order/OrderShopSlice";
import { clearCartBackend, setSelectedCategory } from "../redux/cart/CartSlice";
import {
  clearSalonHomeCartBackend,
  setSelectedSalonHomeCategory,
} from "../redux/cart/HomeSalon";
// import { clearProductCartBackend } from "../redux/cart/CartSlice";
import { clientUpdateById } from "../redux/authentication/AuthenticationSlice";

const PaymentSummary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get all cart states
  const {
    CartItems,
    netPayable,
    cartTotalMrp,
    cartTotalAmount,
    checkoutData,
    selectedCategory,
  } = useSelector((state) => state.Cart);

  const {
    salonHomeCartItems,
    salonHomeNetPayable,
    salonHomeCartTotalMrp,
    salonHomeCartTotalAmount,
    salonHomeCheckoutData,
    salonHomeSelectedCategory,
  } = useSelector((state) => state.HomeSalonCart);

  const {
    productCartItems,
    productNetPayable,
    productCartTotalMrp,
    productCartTotalAmount,
    productCheckoutData,
    salonProductCategory,
  } = useSelector((state) => state.ShopCart);

  const { loginData, currentAddress, address } = useSelector(
    (state) => state.Authentication
  );

  // State variables for ST Coins
  const [useStCoins, setUseStCoins] = useState(false);
  const [stCoinsValue, setStCoinsValue] = useState(0);
  const [stCoinsUsed, setStCoinsUsed] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Get cart type from navigation params
  const cartType = location.state?.cartType || "shopcart";

  // Get current cart data based on type
  const getCurrentCartData = () => {
    switch (cartType) {
      case "shopcart":
        return {
          items: CartItems,
          netPayable,
          totalMrp: cartTotalMrp,
          totalAmount: cartTotalAmount,
          checkoutData,
          category: selectedCategory,
          orderType: "shop",
        };
      case "saloncart":
        return {
          items: salonHomeCartItems,
          netPayable: salonHomeNetPayable,
          totalMrp: salonHomeCartTotalMrp,
          totalAmount: salonHomeCartTotalAmount,
          checkoutData: salonHomeCheckoutData,
          category: salonHomeSelectedCategory,
          orderType: "salon",
        };
      case "productcart":
        return {
          items: productCartItems,
          netPayable: productNetPayable,
          totalMrp: productCartTotalMrp,
          totalAmount: productCartTotalAmount,
          checkoutData: productCheckoutData,
          category: salonProductCategory,
          orderType: "product",
        };
      default:
        return {
          items: [],
          netPayable: 0,
          totalMrp: 0,
          totalAmount: 0,
          checkoutData: {},
          category: "",
          orderType: "shop",
        };
    }
  };

  const currentCart = getCurrentCartData();

  // ST Coins calculation
  useEffect(() => {
    if (useStCoins && loginData?.StCoins > 0) {
      const maxDiscount = Math.min(loginData.StCoins, currentCart.netPayable);
      const actualDiscount = Math.min(maxDiscount, currentCart.netPayable);

      setStCoinsUsed(actualDiscount);
      setStCoinsValue(actualDiscount);
    } else {
      setStCoinsUsed(0);
      setStCoinsValue(0);
    }
  }, [useStCoins, currentCart.netPayable, loginData?.StCoins]);

  // Calculate final amounts
  const calculateFinalAmounts = () => {
    const baseAmount = currentCart.netPayable || 0;
    const stCoinsDiscount = stCoinsValue;
    const finalPayable = Math.max(0, baseAmount - stCoinsDiscount);

    return {
      baseAmount,
      stCoinsDiscount,
      finalPayable,
      totalSavings: currentCart.totalMrp - baseAmount,
    };
  };

  const finalAmounts = calculateFinalAmounts();

  // Error handler
  const showError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 5000);
  };

  // Address validation
  const validateAddress = () => {
    if (!address || address.length === 0) {
      return "Please add an address before proceeding";
    }

    if (!currentAddress) {
      return "Please select a delivery address";
    }

    if (!currentAddress.HNo || !currentAddress.StreetDet) {
      return "Selected address is incomplete. Please choose a complete address";
    }

    if (!currentAddress.City || !currentAddress.Pincode) {
      return "City and Pincode are required in the selected address";
    }

    return null;
  };

  // Payment handler
  const handlePayment = async () => {
    // Validate address first
    const addressError = validateAddress();
    if (addressError) {
      showError(addressError);
      return;
    }

    setLoading(true);
    const { finalPayable } = calculateFinalAmounts();

    console.log("💳 Payment Handler - Final Amounts:", {
      finalPayable,
      stCoinsUsed,
      baseNetPayable: currentCart.netPayable,
    });

    console.log("🔍 PaymentSummary - Current cart items:", currentCart.items);
    console.log(
      "🔍 PaymentSummary - First item SalonName:",
      currentCart.items[0]?.SalonName
    );
    console.log(
      "🔍 PaymentSummary - First item shopName:",
      currentCart.items[0]?.shopName
    );

    // Ensure all cart items have SalonName
    const orderProductsWithSalonName = currentCart.items.map((item) => ({
      ...item,
      SalonName: item.SalonName || item.shopName || "",
      shopName: item.SalonName || item.shopName || "",
    }));

    // Common order data
    const commonOrderData = {
      ClientId: loginData._id,
      CatName: currentCart.category,
      ClientName: loginData.Name,
      TotalAmount: currentCart.totalAmount,
      Email: loginData.Email,
      DeliveryCharge: 0,
      GrandTotal: finalPayable,
      Address: currentCart.checkoutData.Address,
      city: "",
      userLongitude: currentAddress?.Longitude || "",
      userLatitude: currentAddress?.Latitude || "",
      AreaName: currentAddress?.AName || "",
      Mobile: loginData.Mobile,
      PaymentMode: "COD",
      PaymentStatus: "COD",
      TxnId: "NA",
      CurrentStatus: "Not Paid",
      Saving: currentCart.totalMrp - currentCart.netPayable,
      Cashback: "0",
      ServicePreferedDate: currentCart.checkoutData.ServicePreferedDate,
      ServicePreferedTime: currentCart.checkoutData.ServicePreferedTime,
      orderDisplayName: currentCart.checkoutData.orderDisplayName,
      OrderProducts: orderProductsWithSalonName,
      ProductCount: currentCart.items.length,
      SalonName:
        currentCart.items[0]?.SalonName || currentCart.items[0]?.shopName || "",
      shopName:
        currentCart.items[0]?.SalonName || currentCart.items[0]?.shopName || "",
      StCoinsUsed: stCoinsUsed,
    };

    console.log("🔍 PaymentSummary - Final order data:", {
      GrandTotal: commonOrderData.GrandTotal,
      Saving: commonOrderData.Saving,
      StCoinsUsed: commonOrderData.StCoinsUsed,
      TotalAmount: commonOrderData.TotalAmount,
    });

    try {
      let result;

      switch (currentCart.orderType) {
        case "shop":
          console.log("🔄 Creating shop order...");
          result = await dispatch(createOrder(commonOrderData)).unwrap();
          console.log("✅ Shop order result:", result);
          if (result.success) {
            await dispatch(clearCartBackend(loginData._id)).unwrap();
            dispatch(setSelectedCategory(""));
            navigate("/success", {
              state: {
                orderData: commonOrderData,
                orderId: result.order?._id || "temp",
              },
            });
          }
          break;

        case "salon":
          console.log("🔄 Creating salon order...");
          result = await dispatch(
            createHomeSalonOrder(commonOrderData)
          ).unwrap();
          console.log("✅ Salon order result:", result);
          if (result.success) {
            await dispatch(clearSalonHomeCartBackend(loginData._id)).unwrap();
            dispatch(setSelectedSalonHomeCategory(""));

            // PASS THE ORDER DATA TO SUCCESS SCREEN
            navigate("/successsalon", {
              state: {
                orderData: result.order || commonOrderData,
                orderId: result.order?._id || "temp",
              },
            });
          }
          break;

        // case "product":
        //   console.log("🔄 Creating product order...");
        //   result = await dispatch(createProductOrder(commonOrderData)).unwrap();
        //   console.log("✅ Product order result:", result);
        //   if (result.success) {
        //     await dispatch(clearProductCartBackend(loginData._id)).unwrap();
        //     dispatch(setSelectedSalonHomeCategory(""));
        //     navigate("/order-success-product", {
        //       state: {
        //         orderData: commonOrderData,
        //         orderId: result.order?._id || "temp",
        //       },
        //     });
        //   }
        //   break;
      }

      // Update ST Coins if used
      if (useStCoins && result?.success && stCoinsUsed > 0) {
        const remainingStCoins = Math.max(0, loginData.StCoins - stCoinsUsed);
        console.log("🪙 Updating ST Coins:", {
          oldCoins: loginData.StCoins,
          coinsUsed: stCoinsUsed,
          newCoins: remainingStCoins,
        });

        dispatch(
          clientUpdateById({
            _id: loginData._id,
            StCoins: remainingStCoins,
          })
        );
      }
    } catch (error) {
      console.error("❌ Payment error:", error);
      showError("Failed to create order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reusable Components
  const OrderProductItem = ({ item }) => (
    <div className="d-flex border-bottom pb-3 mb-3">
      <img
        src={item.ImgUrl}
        alt={item.ProductName}
        className="rounded-3 me-3"
        style={{ width: "80px", height: "80px", objectFit: "cover" }}
      />
      <div className="flex-grow-1 me-3">
        <h6 className="fw-bold mb-1 text-dark">{item.ProductName}</h6>
        <p className="text-muted small fw-medium mb-2">{item.Brand}</p>

        {item.SalonName && (
          <div
            className="d-flex align-items-center bg-light rounded px-2 py-1 mb-2"
            style={{ width: "fit-content" }}
          >
            <FaStore size={12} className="text-primary me-1" />
            <span className="small fw-semibold text-primary">
              {item.SalonName}
            </span>
          </div>
        )}

        <div className="d-flex gap-3 mb-2">
          {item.Duration && (
            <div className="d-flex align-items-center">
              <FaClock size={12} className="text-muted me-1" />
              <span className="small text-muted">{item.Duration}</span>
            </div>
          )}
          {item.shopName && !item.SalonName && (
            <div className="d-flex align-items-center">
              <FaStore size={12} className="text-muted me-1" />
              <span className="small text-muted">{item.shopName}</span>
            </div>
          )}
        </div>

        {item.Services && (
          <div>
            {item.Services.map((service, index) => (
              <div key={index} className="d-flex align-items-start mb-1">
                <FaCheck size={10} className="text-success mt-1 me-2" />
                <span className="small text-muted">
                  {service.service.length > 35
                    ? `${service.service.substring(0, 35)}...`
                    : service.service}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="text-end">
        <h6 className="fw-bold text-dark mb-1">
          ₹{item.Price || item.TotalAmount}
        </h6>
        {item.Mrp && item.Mrp > (item.Price || item.TotalAmount) && (
          <>
            <p className="text-muted small text-decoration-line-through mb-1">
              ₹{item.Mrp}
            </p>
            <div className="bg-light rounded px-2 py-1">
              <span className="small fw-bold text-primary">
                {Math.round(
                  ((item.Mrp - (item.Price || item.TotalAmount)) / item.Mrp) *
                    100
                )}
                % OFF
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const OrderProductsSection = () => (
    <div className="bg-white rounded-4 p-4 mb-4 border-0 text-start">
      <h5 className="fw-bold mb-3 text-dark">
        {currentCart.orderType === "shop"
          ? "Shop Order"
          : currentCart.orderType === "salon"
          ? "Salon Service Order"
          : "Product Order"}
      </h5>
      {currentCart.items.map((item, index) => (
        <OrderProductItem key={index} item={item} />
      ))}
    </div>
  );

  const ScheduleSummary = () => {
    const scheduleData = currentCart.checkoutData;

    if (
      !scheduleData?.ServicePreferedDate ||
      !scheduleData?.ServicePreferedTime
    ) {
      return null;
    }

    return (
      <div className="bg-white rounded-4 p-4 mb-4 border-0 text-start">
        <h5 className="fw-bold mb-3 text-dark">Schedule Details</h5>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center flex-grow-1">
            <div
              className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: "40px", height: "40px" }}
            >
              <FaCalendarDay size={16} className="text-primary" />
            </div>
            <div>
              <p className="small text-muted mb-1 fw-medium">Service Date</p>
              <p className="fw-bold text-dark mb-0">
                {scheduleData.ServicePreferedDate}
              </p>
            </div>
          </div>

          <div className="border-start mx-3" style={{ height: "50px" }}></div>

          <div className="d-flex align-items-center flex-grow-1">
            <div
              className="bg-light rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: "40px", height: "40px" }}
            >
              <FaClock size={16} className="text-primary" />
            </div>
            <div>
              <p className="small text-muted mb-1 fw-medium">Service Time</p>
              <p className="fw-bold text-dark mb-0">
                {scheduleData.ServicePreferedTime}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const StCoinsSection = () => {
    const availableCoins = loginData?.StCoins || 0;
    const hasCoins = availableCoins > 0;

    return (
      <div className="bg-white rounded-4 p-4 mb-4 border-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-dark">ST Coins Balance</h5>
          <span
            className={`fw-bold ${hasCoins ? "text-primary" : "text-muted"}`}
          >
            {availableCoins} coins
          </span>
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            checked={useStCoins}
            onChange={(e) => hasCoins && setUseStCoins(e.target.checked)}
            disabled={!hasCoins}
            style={{
              backgroundColor: useStCoins ? "#1469A7" : "",
              borderColor: "#1469A7",
            }}
            id="stCoinsCheckbox"
          />
          <label
            className={`form-check-label ${
              !hasCoins ? "text-muted" : "text-dark"
            }`}
            htmlFor="stCoinsCheckbox"
          >
            Use ST Coins for this order
            {!hasCoins && (
              <span className="text-danger fst-italic">
                {" "}
                (No coins available)
              </span>
            )}
          </label>
        </div>
        {useStCoins && stCoinsValue > 0 && (
          <div className="mt-2">
            <p className="text-success small fw-semibold mb-0">
              {stCoinsUsed} coins applied (₹{stCoinsValue} off)
            </p>
          </div>
        )}
      </div>
    );
  };

  const PriceBreakdown = () => {
    const { baseAmount, stCoinsDiscount, finalPayable, totalSavings } =
      calculateFinalAmounts();

    return (
      <div className="bg-white rounded-4 p-4 border-0">
        <h5 className="fw-bold mb-3 text-dark">Price Details</h5>

        <div className="d-flex justify-content-between mb-2">
          <span className="text-muted fw-medium">Total MRP</span>
          <span className="fw-semibold text-dark">
            ₹{currentCart.totalMrp || 0}
          </span>
        </div>

        {totalSavings > 0 && (
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted fw-medium">Total Discount</span>
            <span className="fw-semibold text-success">-₹{totalSavings}</span>
          </div>
        )}

        {stCoinsDiscount > 0 && (
          <div className="d-flex justify-content-between mb-2">
            <span className="text-muted fw-medium">ST Coins Used</span>
            <span className="fw-semibold text-success">
              -₹{stCoinsDiscount}
            </span>
          </div>
        )}

        <div className="d-flex justify-content-between mb-3">
          <span className="text-muted fw-medium">Convenience Fee</span>
          <span className="fw-semibold text-dark">₹0</span>
        </div>

        <div className="bg-light rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start">
            <FaInfoCircle size={14} className="text-muted mt-1 me-2" />
            <p className="small text-muted mb-0">
              Nominal charge for partner training, ensuring superior service,
              support and safety.
            </p>
          </div>
        </div>

        <div className="d-flex justify-content-between border-top pt-3">
          <span className="fw-bold text-dark">Net Payable</span>
          <span className="fw-bold fs-5 text-dark">₹{finalPayable}</span>
        </div>
      </div>
    );
  };

  const EmptyCart = () => (
    <div className="d-flex flex-column align-items-center justify-content-center py-5">
      <FaShoppingCart size={60} className="text-muted mb-3" />
      <h5 className="text-muted mb-3">No items in cart</h5>
      <button className="btn btn-primary" onClick={() => navigate(-1)}>
        Back to Shopping
      </button>
    </div>
  );

  if (currentCart.items.length === 0) {
    return (
      <div className="min-vh-100 bg-light">
        <div
          className="py-4 position-sticky top-0 z-3"
          style={{ backgroundColor: "#1469A7" }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="btn btn-link text-white p-0 border-0"
                >
                  <FaChevronLeft size={24} />
                </button>
              </div>
              <div className="col text-center">
                <h5 className="mb-0 fw-bold text-white">Payment Summary</h5>
              </div>
              <div className="col-auto" style={{ width: "40px" }}></div>
            </div>
          </div>
        </div>

        <div className="container py-4">
          <EmptyCart />
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div
        className="py-4 position-sticky top-0 z-3"
        style={{ backgroundColor: "#1469A7" }}
      >
        <div className="container">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link text-white p-0 border-0"
              >
                <FaChevronLeft size={24} />
              </button>
            </div>
            <div className="col text-center">
              <h5 className="mb-0 fw-bold text-white">Payment Summary</h5>
            </div>
            <div className="col-auto" style={{ width: "40px" }}></div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {errorMessage && (
        <div style={{ backgroundColor: "#b93c5b" }}>
          <div className="container">
            <div className="d-flex align-items-center justify-content-center py-2">
              <FaExclamationCircle className="me-2" size={16} />
              <span className="fw-semibold text-white">{errorMessage}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="container py-4">
        <div className="row">
          <div className="col-lg-8">
            <AddressComponent />
            <OrderProductsSection />
            <StCoinsSection />
          </div>
          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: "100px" }}>
              <ScheduleSummary />
              <PriceBreakdown />

              {/* Proceed Button */}
              <div className="mt-4">
                <button
                  className={`btn w-100 py-3 fw-bold rounded-3 border-0 text-white ${
                    loading || !currentAddress ? "btn-secondary" : ""
                  }`}
                  style={{
                    backgroundColor:
                      loading || !currentAddress ? "#6c757d" : "#b93c5b",
                  }}
                  onClick={handlePayment}
                  disabled={loading || !currentAddress}
                >
                  {loading ? (
                    <>
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></div>
                      PROCESSING...
                    </>
                  ) : (
                    <>
                      PROCEED TO PAY ₹{finalAmounts.finalPayable}
                      <FaArrowRight className="ms-2" />
                    </>
                  )}
                </button>
                {!currentAddress && (
                  <div
                    className="alert alert-warning mt-2 py-2 small"
                    role="alert"
                  >
                    <FaExclamationCircle className="me-2" />
                    Please select a delivery address to proceed
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSummary;
